import { useEffect, useState, useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { pageAPI, workspaceAPI } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { NotionEditor } from '../components/NotionEditor';
import {
  Plus, FileText, Star, Trash2, Clock, Loader2, Image as ImageIcon,
  CheckSquare, Users, FolderKanban, Calendar, Sparkles, Library, Building2, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { formatDateLong } from '../lib/dateFormat';

const TEMPLATE_ICONS = { FileText, CheckSquare, Users, FolderKanban, Calendar, Sparkles, Library, Building2 };

const COVER_IMAGES = [
  'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&h=300&fit=crop',
  'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=1200&h=300&fit=crop',
  'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=300&fit=crop',
  'https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?w=1200&h=300&fit=crop',
  'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=1200&h=300&fit=crop',
  'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&h=300&fit=crop',
];

export default function PagesPage() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [activePage, setActivePage] = useState(null);
  const [loadingPage, setLoadingPage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [saveTimeout, setSaveTimeoutState] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    api.get('/pages/templates/list').then(res => setTemplates(res.data)).catch(() => {});
    if (currentWorkspace?.id) {
      workspaceAPI.listMembers(currentWorkspace.id).then(res => {
        setTeamMembers(res.data.map(m => ({ id: m.user_id || m.user?.id, name: m.user?.name || m.user?.email || '', email: m.user?.email || '', role: m.role || '' })));
      }).catch(() => {});
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    const pageId = searchParams.get('id');
    if (pageId && currentWorkspace?.id) openPage(pageId);
  }, [searchParams, currentWorkspace?.id]);

  const openPage = async (pageId) => {
    if (!currentWorkspace?.id) return;
    setLoadingPage(true);
    try {
      const res = await pageAPI.get(currentWorkspace.id, pageId);
      setActivePage(res.data);
      setTitle(res.data.title || '');
    } catch { toast.error('Failed to load page'); }
    finally { setLoadingPage(false); }
  };

  const createFromTemplate = async (templateId) => {
    if (!currentWorkspace?.id) return;
    const template = templates.find(t => t.id === templateId);
    let content = { type: 'doc', content: [{ type: 'paragraph' }] };
    let pageTitle = template?.name || 'Untitled';

    if (templateId === 'meeting_notes') {
      content = { type: 'doc', content: [
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Attendees' }] },
        { type: 'bulletList', content: [{ type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }] },
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Agenda' }] },
        { type: 'bulletList', content: [{ type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }] },
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Action Items' }] },
        { type: 'taskList', content: [{ type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }] },
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Notes' }] },
        { type: 'paragraph' },
      ]};
    } else if (templateId === 'tasks_tracker') {
      content = { type: 'doc', content: [
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'In Progress' }] },
        { type: 'taskList', content: [{ type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }] },
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'To Do' }] },
        { type: 'taskList', content: [{ type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }] },
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Completed' }] },
        { type: 'taskList', content: [{ type: 'taskItem', attrs: { checked: true }, content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }] },
      ]};
    } else if (templateId === 'project_brief') {
      content = { type: 'doc', content: [
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Project Overview' }] },
        { type: 'paragraph', content: [{ type: 'text', text: 'Describe the project goals and scope...' }] },
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Timeline' }] },
        { type: 'bulletList', content: [
          { type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Start date: ' }] }] },
          { type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'End date: ' }] }] },
        ]},
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Deliverables' }] },
        { type: 'taskList', content: [{ type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }] },
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Team' }] },
        { type: 'bulletList', content: [{ type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: '' }] }] }] },
      ]};
    } else if (templateId === 'progress_tracker') {
      pageTitle = 'Progress Tracker';
      content = { type: 'doc', content: [
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Project Milestones' }] },
        { type: 'taskList', content: [
          { type: 'taskItem', attrs: { checked: true }, content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Phase 1: Discovery & Research — Completed March 10th' }] }] },
          { type: 'taskItem', attrs: { checked: true }, content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Phase 2: Design mockups — Completed March 18th' }] }] },
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Phase 3: Development sprint 1 — Due April 5th' }] }] },
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Phase 4: Client review & QA — Due April 15th' }] }] },
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Phase 5: Launch — Due April 22nd' }] }] },
        ]},
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Blockers' }] },
        { type: 'bulletList', content: [{ type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Waiting on client asset approval' }] }] }] },
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Notes' }] },
        { type: 'paragraph', content: [{ type: 'text', text: 'Overall progress: 40% complete. On track for April launch.' }] },
      ]};
    } else if (templateId === 'time_management') {
      pageTitle = 'Weekly Time Management';
      content = { type: 'doc', content: [
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Monday' }] },
        { type: 'taskList', content: [
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: '9:00–10:30 AM — Deep work: Content writing (Client A)' }] }] },
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: '11:00 AM — Team standup call' }] }] },
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: '1:00–3:00 PM — Design review session' }] }] },
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: '3:30–5:00 PM — Client emails & follow-ups' }] }] },
        ]},
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Tuesday' }] },
        { type: 'taskList', content: [
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: '9:00–11:00 AM — Strategy planning (Client B)' }] }] },
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: '1:00 PM — Client meeting: Korea House' }] }] },
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: '2:30–4:30 PM — Creative asset production' }] }] },
        ]},
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Focus Priorities This Week' }] },
        { type: 'orderedList', content: [
          { type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Complete Q2 campaign assets — 8 hours estimated' }] }] },
          { type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Finalize client proposals — 4 hours estimated' }] }] },
          { type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Review team submissions — 2 hours estimated' }] }] },
        ]},
      ]};
    } else if (templateId === 'content_plan') {
      pageTitle = 'Monthly Content Plan';
      content = { type: 'doc', content: [
        { type: 'heading', attrs: { level: 1 }, content: [{ type: 'text', text: 'April 2026 Content Plan' }] },
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Week 1 (April 1–7)' }] },
        { type: 'taskList', content: [
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Instagram Reel: Behind the scenes at studio — April 1st' }] }] },
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Facebook Post: Client testimonial graphic — April 3rd' }] }] },
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: 'LinkedIn Article: Industry trends Q2 — April 5th' }] }] },
        ]},
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Week 2 (April 8–14)' }] },
        { type: 'taskList', content: [
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Instagram Story: Team spotlight — April 8th' }] }] },
          { type: 'taskItem', attrs: { checked: false }, content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Blog Post: How we manage 10+ clients — April 10th' }] }] },
        ]},
        { type: 'heading', attrs: { level: 2 }, content: [{ type: 'text', text: 'Content Guidelines' }] },
        { type: 'bulletList', content: [
          { type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Brand voice: Professional, warm, knowledgeable' }] }] },
          { type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Hashtags: #StellarProductions #SocialMediaManagement #ReginaMarketing' }] }] },
          { type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Image style: Clean, bright, on-brand colors' }] }] },
        ]},
      ]};
    }

    try {
      const res = await pageAPI.create({ workspace_id: currentWorkspace.id, title: pageTitle, content });
      setShowTemplates(false);
      setSearchParams({ id: res.data.id });
    } catch { toast.error('Failed to create page'); }
  };

  const autoSave = (field, value) => {
    if (saveTimeout) clearTimeout(saveTimeout);
    const t = setTimeout(() => {
      if (activePage && currentWorkspace?.id) {
        setSaving(true);
        pageAPI.update(currentWorkspace.id, activePage.id, { [field]: value })
          .then(() => setSaving(false))
          .catch(() => setSaving(false));
      }
    }, 800);
    setSaveTimeoutState(t);
  };

  const handleTitleChange = (v) => { setTitle(v); autoSave('title', v); };
  const handleContentChange = (content) => { autoSave('content', content); };

  const setCoverImage = async (url) => {
    if (!activePage || !currentWorkspace?.id) return;
    try {
      await pageAPI.update(currentWorkspace.id, activePage.id, { cover_image: url });
      setActivePage(p => ({ ...p, cover_image: url }));
      setShowCoverPicker(false);
    } catch {}
  };

  const removeCover = async () => {
    if (!activePage || !currentWorkspace?.id) return;
    await pageAPI.update(currentWorkspace.id, activePage.id, { cover_image: '' });
    setActivePage(p => ({ ...p, cover_image: '' }));
  };

  // Landing state — show template picker
  if (!activePage && !loadingPage) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-2xl w-full space-y-8">
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-display font-bold tracking-tight">Create a new page</h1>
            <p className="text-sm text-muted-foreground">Start with a blank page or choose a template</p>
          </div>

          {/* Quick options */}
          <div className="grid grid-cols-3 gap-3">
            <button onClick={() => createFromTemplate('empty')} className="p-5 rounded-xl border border-border/50 bg-card/40 hover:bg-secondary/30 hover:border-border transition-colors text-left" data-testid="template-empty">
              <FileText className="h-6 w-6 text-muted-foreground mb-3" />
              <p className="text-sm font-medium">Empty page</p>
            </button>
            <button onClick={() => setShowTemplates(true)} className="p-5 rounded-xl border border-border/50 bg-card/40 hover:bg-secondary/30 hover:border-border transition-colors text-left" data-testid="template-database">
              <CheckSquare className="h-6 w-6 text-muted-foreground mb-3" />
              <p className="text-sm font-medium">From template</p>
            </button>
            <button onClick={() => createFromTemplate('empty')} className="p-5 rounded-xl border border-border/50 bg-card/40 hover:bg-secondary/30 hover:border-border transition-colors text-left">
              <Sparkles className="h-6 w-6 text-muted-foreground mb-3" />
              <p className="text-sm font-medium">Build with AI</p>
            </button>
          </div>

          {/* Template cards */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground/50 uppercase tracking-wider mb-3">Suggested</p>
            <div className="grid grid-cols-2 gap-3">
              {templates.filter(t => t.category === 'suggested').map(t => {
                const Icon = TEMPLATE_ICONS[t.icon] || FileText;
                return (
                  <button key={t.id} onClick={() => createFromTemplate(t.id)}
                    className="p-4 rounded-xl border border-border/50 hover:border-border bg-card/40 hover:bg-secondary/20 transition-colors text-left group"
                    data-testid={`template-${t.id}`}
                  >
                    <div className="h-2 w-full rounded-full mb-4" style={{ backgroundColor: t.color || 'hsl(var(--muted-foreground))', opacity: 0.6 }} />
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="h-4 w-4" style={{ color: t.color }} />
                      <p className="text-sm font-semibold">{t.name}</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground">{t.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Templates dialog */}
        <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle className="font-display">Choose a template</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-4">
              {templates.map(t => {
                const Icon = TEMPLATE_ICONS[t.icon] || FileText;
                return (
                  <button key={t.id} onClick={() => createFromTemplate(t.id)}
                    className="p-4 rounded-xl border border-border/50 hover:border-border bg-card/40 hover:bg-secondary/20 transition-colors text-left"
                  >
                    <Icon className="h-5 w-5 mb-2" style={{ color: t.color }} />
                    <p className="text-sm font-semibold">{t.name}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{t.description}</p>
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (loadingPage) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto" data-testid="page-editor">
      {/* Cover Image */}
      {activePage?.cover_image ? (
        <div className="relative h-[200px] w-full group">
          <img src={activePage.cover_image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
          <div className="absolute bottom-3 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button size="sm" variant="secondary" className="h-7 text-[11px]" onClick={() => setShowCoverPicker(true)}>Change cover</Button>
            <Button size="sm" variant="secondary" className="h-7 text-[11px]" onClick={removeCover}>Remove</Button>
          </div>
        </div>
      ) : (
        <div className="h-4" /> /* spacer when no cover */
      )}

      {/* Page content */}
      <div className="max-w-3xl mx-auto px-8 py-8">
        {/* Add cover + icon controls */}
        {!activePage?.cover_image && (
          <div className="flex gap-2 mb-4 opacity-0 hover:opacity-100 transition-opacity">
            <button onClick={() => setShowCoverPicker(true)} className="text-[12px] text-muted-foreground/50 hover:text-muted-foreground flex items-center gap-1">
              <ImageIcon className="h-3.5 w-3.5" /> Add cover
            </button>
          </div>
        )}

        {/* Icon + Title */}
        {activePage?.icon && (
          <span className="text-5xl mb-3 block">{activePage.icon}</span>
        )}
        <input
          value={title}
          onChange={e => handleTitleChange(e.target.value)}
          placeholder="Untitled"
          className="w-full text-[40px] font-display font-bold bg-transparent border-none outline-none placeholder:text-muted-foreground/20 mb-1 leading-tight"
          data-testid="page-title-input"
        />

        {/* Properties row */}
        {activePage?.properties && Object.keys(activePage.properties).length > 0 && (
          <div className="flex items-center gap-4 text-[13px] text-muted-foreground mb-6">
            {activePage.properties.status && (
              <span className="flex items-center gap-1.5"><span className="text-muted-foreground/50">Status</span> <Badge variant="outline" className="text-[11px]">{activePage.properties.status}</Badge></span>
            )}
            {activePage.properties.assignee && (
              <span className="flex items-center gap-1.5"><span className="text-muted-foreground/50">Assignee</span> {activePage.properties.assignee}</span>
            )}
            {activePage.properties.due_date && (
              <span className="flex items-center gap-1.5"><span className="text-muted-foreground/50">Due</span> {activePage.properties.due_date}</span>
            )}
          </div>
        )}

        {/* Save indicator */}
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground/40 mb-6">
          {saving && <><Loader2 className="h-3 w-3 animate-spin" /> Saving...</>}
          {!saving && activePage?.updated_at && <><Clock className="h-3 w-3" /> Edited {formatDateLong(activePage.updated_at)}</>}
        </div>

        {/* Block Editor */}
        <NotionEditor
          key={activePage?.id}
          content={activePage?.content}
          onUpdate={handleContentChange}
          placeholder="Type '/' for commands, '@' to mention someone..."
          teamMembers={teamMembers}
        />
      </div>

      {/* Cover picker */}
      <Dialog open={showCoverPicker} onOpenChange={setShowCoverPicker}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Choose a cover</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-2 py-4">
            {COVER_IMAGES.map((url, i) => (
              <button key={i} onClick={() => setCoverImage(url)} className="aspect-video rounded-lg overflow-hidden border-2 border-transparent hover:border-primary transition-colors">
                <img src={url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Or paste an image URL:</p>
            <div className="flex gap-2">
              <Input id="cover-url" placeholder="https://..." className="flex-1" />
              <Button size="sm" onClick={() => { const v = document.getElementById('cover-url').value; if (v) setCoverImage(v); }}>Apply</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
